import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  EmojiEvents as TrophyIcon,
  Speed as SpeedIcon,
  Stars as StarsIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

// Trend Indicator Component
export const TrendIndicator = ({ value, label, size = 'small' }) => {
  if (value === null || value === undefined) return null;
  
  const isPositive = value > 0;
  const isNeutral = value === 0;
  
  return (
    <Chip
      icon={isNeutral ? null : (isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />)}
      label={`${isPositive ? '+' : ''}${value}% ${label}`}
      size={size}
      sx={{
        backgroundColor: isNeutral ? '#f3f4f6' : (isPositive ? '#d1fae5' : '#fee2e2'),
        color: isNeutral ? '#6b7280' : (isPositive ? '#065f46' : '#991b1b'),
        fontWeight: 600,
        fontSize: '0.7rem', // ✅ Reduced font size
        height: 22,
        '& .MuiChip-icon': {
          color: isNeutral ? '#6b7280' : (isPositive ? '#065f46' : '#991b1b'),
        }
      }}
    />
  );
};

// Top Performers Card - ✅ FIXED: Reduced fonts, shows top 3 with image stats
export const TopPerformersCard = ({ data, loading }) => {
  if (loading) {
    return (
      <Card elevation={3} sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: '0.95rem' }}>
            <TrophyIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#fbbf24', fontSize: '1.2rem' }} />
            Top Performing Facilities
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card elevation={3} sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: '0.95rem' }}>
            <TrophyIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#fbbf24', fontSize: '1.2rem' }} />
            Top Performing Facilities
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>No data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const getPerformanceColor = (index) => {
    if (index === 0) return '#fbbf24'; // Gold
    if (index === 1) return '#9ca3af'; // Silver
    if (index === 2) return '#cd7f32'; // Bronze
    return '#e5e7eb';
  };

  // ✅ Show only top 3 facilities
  const topThree = data.slice(0, 3);

  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.95rem' }}>
          <TrophyIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#fbbf24', fontSize: '1.2rem' }} />
          Top Performing Facilities
        </Typography>
        <List sx={{ p: 0 }}>
          {topThree.map((facility, idx) => (
            <ListItem 
              key={idx}
              sx={{
                borderRadius: 1,
                mb: 1,
                p: 1.5,
                '&:hover': {
                  backgroundColor: '#f9fafb',
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: getPerformanceColor(idx), fontWeight: 700, width: 36, height: 36, fontSize: '1rem' }}>
                  {idx + 1}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {facility.facility_name}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.75rem' }}>
                      Quality: {facility.quality_score}% | Screenings: {facility.total_screenings}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }} color="text.secondary">
                      {facility.nurses_region} • With Images: {facility.forms_with_images}/{facility.total_screenings}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Alerts Widget - ✅ FIXED: Reduced fonts, working View All
export const AlertsWidget = ({ alerts, loading, onViewAll }) => {
  if (loading) {
    return (
      <Card elevation={3} sx={{ border: '2px solid #ef4444', height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <WarningIcon sx={{ color: '#ef4444', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              High-Risk Alerts
            </Typography>
          </Box>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (!alerts) {
    return (
      <Card elevation={3} sx={{ border: '2px solid #ef4444', height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <WarningIcon sx={{ color: '#ef4444', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              High-Risk Alerts
            </Typography>
          </Box>
          <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>No alerts</Typography>
        </CardContent>
      </Card>
    );
  }

  const totalAlerts = alerts.critical_eye + alerts.critical_foot + alerts.overdue;

  return (
    <Card 
      elevation={3} 
      sx={{ 
        border: totalAlerts > 0 ? '2px solid #ef4444' : '2px solid #10b981',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon sx={{ color: totalAlerts > 0 ? '#ef4444' : '#10b981', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              High-Risk Alerts
            </Typography>
          </Box>
          {/* ✅ FIXED: Working View All button */}
          {totalAlerts > 0 && (
            <Tooltip title="View all high-risk patients">
              <IconButton 
                size="small"
                onClick={onViewAll}
                sx={{ 
                  backgroundColor: '#f3f4f6',
                  '&:hover': { backgroundColor: '#e5e7eb' }
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="h3" sx={{ color: '#ef4444', fontWeight: 800, fontSize: '1.8rem' }}>
                {alerts.critical_eye}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }} color="text.secondary">
                Critical Eye
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="h3" sx={{ color: '#f59e0b', fontWeight: 800, fontSize: '1.8rem' }}>
                {alerts.critical_foot}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }} color="text.secondary">
                Critical Foot
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="h3" sx={{ color: '#6366f1', fontWeight: 800, fontSize: '1.8rem' }}>
                {alerts.overdue}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }} color="text.secondary">
                Overdue
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {totalAlerts === 0 && (
          <Box mt={1.5} textAlign="center">
            <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              ✓ All clear - No critical alerts
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Performance Score Card - ✅ FIXED: Reduced fonts
export const PerformanceScoreCard = ({ score, label, target = 80 }) => {
  const getColor = (score) => {
    if (score >= target) return '#10b981';
    if (score >= target * 0.7) return '#f59e0b';
    return '#ef4444';
  };

  const getGrade = (score) => {
    if (score >= 90) return 'A+';
    if (score >= 80) return 'A';
    if (score >= 70) return 'B';
    if (score >= 60) return 'C';
    return 'D';
  };

  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <SpeedIcon sx={{ color: getColor(score), fontSize: 18 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {label}
          </Typography>
        </Box>
        <Box display="flex" alignItems="baseline" gap={1} mb={1}>
          <Typography variant="h2" sx={{ fontWeight: 800, color: getColor(score), fontSize: '2rem' }}>
            {score}
          </Typography>
          <Typography variant="h4" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
            / 100
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Chip 
            label={`Grade: ${getGrade(score)}`} 
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              backgroundColor: getColor(score),
              color: 'white',
              fontWeight: 700,
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Target: {target}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={Math.min(score, 100)} 
          sx={{
            height: 6,
            borderRadius: 4,
            backgroundColor: '#e5e7eb',
            '& .MuiLinearProgress-bar': {
              backgroundColor: getColor(score),
            }
          }}
        />
      </CardContent>
    </Card>
  );
};

// Insights Card - ✅ FIXED: Reduced fonts
export const InsightsCard = ({ insights, loading }) => {
  if (loading) {
    return (
      <Card elevation={3} sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.95rem' }}>
            <StarsIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#8b5cf6', fontSize: '1.2rem' }} />
            Key Insights
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (!insights || insights.length === 0) {
    return (
      <Card elevation={3} sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.95rem' }}>
            <StarsIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#8b5cf6', fontSize: '1.2rem' }} />
            Key Insights
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>No insights available yet</Typography>
        </CardContent>
      </Card>
    );
  }

  const getInsightColor = (type) => {
    switch (type) {
      case 'positive': return { bg: '#d1fae5', text: '#065f46', icon: '✓' };
      case 'warning': return { bg: '#fef3c7', text: '#92400e', icon: '⚠' };
      case 'alert': return { bg: '#fee2e2', text: '#991b1b', icon: '!' };
      default: return { bg: '#e0e7ff', text: '#3730a3', icon: 'i' };
    }
  };

  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.95rem' }}>
          <StarsIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#8b5cf6', fontSize: '1.2rem' }} />
          Key Insights
        </Typography>
        <Box display="flex" flexDirection="column" gap={1}>
          {insights.map((insight, idx) => {
            const colors = getInsightColor(insight.type);
            return (
              <Box 
                key={idx}
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: colors.bg,
                  border: `1px solid ${colors.text}20`,
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Typography 
                    sx={{ 
                      fontWeight: 700, 
                      fontSize: '1rem',
                      color: colors.text,
                      minWidth: 20,
                    }}
                  >
                    {colors.icon}
                  </Typography>
                  <Box flex={1}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: colors.text,
                        lineHeight: 1.4,
                        fontSize: '0.8rem', // ✅ Reduced
                      }}
                    >
                      {insight.message}
                    </Typography>
                    {insight.category && (
                      <Chip 
                        label={insight.category}
                        size="small"
                        sx={{
                          mt: 0.5,
                          height: 18,
                          fontSize: '0.65rem',
                          backgroundColor: 'white',
                          color: colors.text,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

// Benchmark Comparison Card - ✅ FIXED: Reduced fonts
export const BenchmarkCard = ({ benchmark, loading }) => {
  if (loading) {
    return (
      <Card elevation={3} sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.95rem' }}>
            Performance vs National Average
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (!benchmark) {
    return (
      <Card elevation={3} sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.95rem' }}>
            Performance vs National Average
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>No benchmark data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const metrics = [
    {
      label: 'Referral Rate',
      value: benchmark.referral_rate,
      national: benchmark.national_referral_rate,
      variance: benchmark.referral_variance,
    },
    {
      label: 'Acceptance Rate',
      value: benchmark.acceptance_rate,
      national: benchmark.national_acceptance_rate,
      variance: benchmark.acceptance_variance,
    },
    {
      label: 'Screening Volume',
      value: benchmark.screenings,
      national: benchmark.national_avg_screenings,
      variance: benchmark.volume_variance,
    },
  ];

  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.95rem' }}>
          Performance vs National Average
        </Typography>
        <Box display="flex" flexDirection="column" gap={1.5}>
          {metrics.map((metric, idx) => (
            <Box key={idx}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {metric.label}
                </Typography>
                <TrendIndicator value={metric.variance} label="" size="small" />
              </Box>
              <Box display="flex" gap={2} alignItems="center">
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Your facility
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {typeof metric.value === 'number' ? metric.value.toFixed(1) : metric.value}
                  </Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    National avg
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#6b7280', fontSize: '1.1rem' }}>
                    {typeof metric.national === 'number' ? metric.national.toFixed(1) : metric.national}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default {
  TrendIndicator,
  TopPerformersCard,
  AlertsWidget,
  PerformanceScoreCard,
  InsightsCard,
  BenchmarkCard,
};